import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Suspense } from 'react';
import { Toaster } from 'sonner';
import { meQuery } from '../features/auth/queries.ts';
import { validateCollectionSearch } from '../features/collections/search.ts';
import { queryClient } from '../lib/query.ts';
import {
  AccountPage,
  AppShell,
  AssetsPage,
  AudiencePage,
  BuilderPage,
  ConsolePage,
  JoinPage,
  LoginPage,
  PairPage,
  PresentationsPage,
  PreviewPage,
  ProjectorPage,
  RegisterPage,
  RemotePage,
  ReportPage,
  SessionsPage,
  TopicDetailPage,
  TopicsPage,
  UsersPage,
} from './lazy-pages.tsx';
import { NotFoundPage } from './not-found.tsx';
import { PageFallback } from './page-fallback.tsx';

interface RouterContext {
  queryClient: QueryClient;
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
      <Toaster position="bottom-right" richColors closeButton />
    </>
  ),
  notFoundComponent: NotFoundPage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/app/presentations' });
  },
});

const redirectIfLoggedIn = async ({ context }: { context: RouterContext }) => {
  const user = await context.queryClient.ensureQueryData(meQuery);
  if (user) throw redirect({ to: '/app/presentations' });
};

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>) =>
    typeof search.redirect === 'string' ? { redirect: search.redirect } : {},
  beforeLoad: redirectIfLoggedIn,
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  beforeLoad: redirectIfLoggedIn,
  component: RegisterPage,
});

export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(meQuery);
    if (!user) throw redirect({ to: '/login', search: { redirect: location.href } });
    return { user };
  },
  component: AppShell,
});

const appIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/app/presentations' });
  },
});

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/account',
  component: AccountPage,
});
const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin/users',
  beforeLoad: ({ context }) => {
    if (context.user.role !== 'ADMIN') throw redirect({ to: '/app/presentations' });
  },
  component: UsersPage,
});
const assetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/assets',
  validateSearch: validateCollectionSearch,
  component: AssetsPage,
});
const topicsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/topics',
  validateSearch: validateCollectionSearch,
  component: TopicsPage,
});
const topicDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/topics/$topicId',
  validateSearch: validateCollectionSearch,
  component: function TopicDetailRoute() {
    const { topicId } = topicDetailRoute.useParams();
    return <TopicDetailPage key={topicId} topicId={topicId} />;
  },
});
const presentationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/presentations',
  validateSearch: validateCollectionSearch,
  component: PresentationsPage,
});
const builderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/presentations/$presentationId',
  validateSearch: validateCollectionSearch,
  component: function BuilderRoute() {
    const { presentationId } = builderRoute.useParams();
    return <BuilderPage key={presentationId} presentationId={presentationId} />;
  },
});
const previewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/presentations/$presentationId/preview',
  validateSearch: (search: Record<string, unknown>): { revision?: string } =>
    typeof search.revision === 'string' ? { revision: search.revision } : {},
  component: function PreviewRoute() {
    const { presentationId } = previewRoute.useParams();
    const { revision } = previewRoute.useSearch();
    return <PreviewPage presentationId={presentationId} revisionId={revision} />;
  },
});

const sessionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sessions',
  validateSearch: validateCollectionSearch,
  component: SessionsPage,
});
const reportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sessions/$sessionId',
  validateSearch: validateCollectionSearch,
  component: function ReportRoute() {
    const { sessionId } = reportRoute.useParams();
    return <ReportPage key={sessionId} sessionId={sessionId} />;
  },
});

const consoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/present/$sessionId',
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(meQuery);
    if (!user) throw redirect({ to: '/login', search: { redirect: location.href } });
  },
  component: function ConsoleRoute() {
    const { sessionId } = consoleRoute.useParams();
    return <ConsolePage key={sessionId} sessionId={sessionId} />;
  },
});
const projectorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/present/$sessionId/projector',
  component: function ProjectorRoute() {
    const { sessionId } = projectorRoute.useParams();
    return <ProjectorPage key={sessionId} sessionId={sessionId} />;
  },
});
const remoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/present/$sessionId/remote',
  component: function RemoteRoute() {
    const { sessionId } = remoteRoute.useParams();
    return <RemotePage key={sessionId} sessionId={sessionId} />;
  },
});
const pairRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pair/$token',
  component: function PairRoute() {
    const { token } = pairRoute.useParams();
    return <PairPage token={token} />;
  },
});
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join',
  component: () => <JoinPage />,
});
const joinCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join/$code',
  component: function JoinCodeRoute() {
    const { code } = joinCodeRoute.useParams();
    return <JoinPage code={code} />;
  },
});
const legacyJoinCodeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/j/$code',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/join/$code', params: { code: params.code }, replace: true });
  },
});
const audienceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/live/$sessionId',
  component: function AudienceRoute() {
    const { sessionId } = audienceRoute.useParams();
    return <AudiencePage key={sessionId} sessionId={sessionId} />;
  },
});

const routeTree = rootRoute.addChildren([
  consoleRoute,
  projectorRoute,
  remoteRoute,
  pairRoute,
  joinRoute,
  joinCodeRoute,
  legacyJoinCodeRoute,
  audienceRoute,
  indexRoute,
  loginRoute,
  registerRoute,
  appRoute.addChildren([
    appIndexRoute,
    accountRoute,
    usersRoute,
    assetsRoute,
    topicsRoute,
    topicDetailRoute,
    presentationsRoute,
    builderRoute,
    previewRoute,
    sessionsRoute,
    reportRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
